// Storefront v2 motion (planning/022-storefront-v2 stage 12, guide §05): the JS half of the
// catalogue. Reveal fallback with the 60ms stagger (M04), corner-arc draw (M03), blur-up image
// load (M50), card scene swap (M11) and the card → PDP image morph hook (M53). M07 lives in
// am-chrome.js, M30/M31/M34 in am-product.js. No scroll listeners, no copy, no libraries.
// `am-js` and `am-io` (IntersectionObserver reveal, i.e. no scroll-driven CSS or reduced motion)
// are set on <html> by an inline script in snippets/theme-tokens.liquid, before first paint.

const root = document.documentElement;
// checks in with the inline script's 3s fail-safe, which otherwise drops am-js/am-io
root.setAttribute('data-am-motion', '');
const fallback = root.classList.contains('am-io');

// M50: a lazy print fades (and unblurs) in over its 40px LQIP once loaded. Capture phase, because
// load doesn't bubble; this also covers prints added later (filters, load more, recs, cart).
const settle = (e) => e.target.classList?.contains('am-lqip') && e.target.classList.add('is-loaded');
document.addEventListener('load', settle, true);
document.addEventListener('error', settle, true);
for (const img of document.querySelectorAll('img.am-lqip')) if (img.complete) img.classList.add('is-loaded');

// M04 (fallback) + M03: reveal once at 15% visible. Elements entering together stagger by 60ms,
// at most 6 steps.
const io =
  'IntersectionObserver' in window &&
  new IntersectionObserver(
    (entries) => {
      let i = 0;
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        const el = e.target;
        if (el.hasAttribute('data-am-draw')) el.classList.add('is-drawn');
        else {
          el.style.setProperty('--i', Math.min(i++, 5));
          el.classList.add('is-in');
        }
        io.unobserve(el);
      }
    },
    { threshold: 0.15 }
  );

const show = (el, cls) => (io ? io.observe(el) : el.classList.add(cls));
function watch(scope) {
  for (const el of scope.querySelectorAll('[data-am-draw]:not(.is-drawn)')) show(el, 'is-drawn');
  if (fallback) for (const el of scope.querySelectorAll('[data-reveal]:not(.is-in)')) show(el, 'is-in');
}
watch(document);
// sections swapped in later (collection filters, load more, recommendations) bring new targets
new MutationObserver((records) => {
  for (const r of records) for (const n of r.addedNodes) if (n.nodeType === 1) watch(n.parentElement || n);
}).observe(document.body, { childList: true, subtree: true });

// M11: a card with a scene loads it on first hover or focus (desktop pointers), then CSS fades it in.
const hover = matchMedia('(hover: hover)');
function loadScene(e) {
  const box = e.target.closest?.('.am-card')?.querySelector('[data-am-scene]');
  if (!box || box.firstChild || !hover.matches) return;
  const img = new Image();
  img.alt = '';
  img.sizes = box.dataset.sizes;
  img.srcset = box.dataset.srcset;
  img.src = box.dataset.src;
  img.addEventListener('load', () => img.classList.add('is-ready'), { once: true });
  box.append(img);
}
document.addEventListener('pointerover', loadScene);
document.addEventListener('focusin', loadScene);

// M53: the print of the clicked product link morphs into the PDP print. Horizon's
// view-transitions.js turns the triggered element's type into the transition type; the PDP print
// carries the same type statically. Only one element per page may be named.
document.addEventListener('click', (e) => {
  const link = e.target.closest?.('a[href*="/products/"]');
  const paper = link?.querySelector('.am-print__paper');
  if (!paper || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey) return;
  for (const el of document.querySelectorAll('[data-view-transition-triggered]')) {
    el.removeAttribute('data-view-transition-triggered');
    el.removeAttribute('data-view-transition-type');
  }
  paper.setAttribute('data-view-transition-type', 'product-image-transition');
  paper.setAttribute('data-view-transition-triggered', 'true');
});
